import React, { useEffect, useState, useMemo } from 'react';
import { ComparisonTable } from './ComparisonTable';
import { structureDataTableRow } from '../../utils/comparison/helpers';
import { Div, Button, DivProps } from '@kizi/core-components';
import { ProductComparisonLabel } from '../comparison/ProductComparisonContainer';
import { usePage } from '@kizi/comparator-core';

export interface ProductComparisonProps extends DivProps {
  products: any[];
  labels: Array<ProductComparisonLabel>;
  handler: (
    product,
    labels,
    priceMode,
    additionalData,
    products
  ) => (
    | {
        content: any;
        isVisible: boolean;
        toggleable: boolean;
        value?: string | number;
      }
    | { label: string; children: any[] }
  )[];
  showMoreButtonLabel?: string;
  showLessButtonLabel?: string;
  withToggleButton?: boolean;
  filter?: string;
  filterOptionsHandler: (options) => void;
  priceMode: string;
  options: string[];
  additionalData?: any;
}

export const ProductComparison: React.FC<ProductComparisonProps> = ({
  products,
  labels,
  handler,
  withToggleButton = false,
  showMoreButtonLabel,
  showLessButtonLabel,
  filter,
  filterOptionsHandler,
  priceMode,
  options,
  additionalData,
  ...divProps
}) => {
  const { state, setState } = usePage();

  const getProductDataColumns = (_products) =>
    _products.map((product) =>
      handler(product, labels, priceMode, additionalData, _products)
    );

  const [showButtonLabel, setShowButtonLabel] = useState(showMoreButtonLabel);

  const [productDataColumns, setProductDataColumns] = useState(
    getProductDataColumns(products)
  );

  useEffect(() => {
    setProductDataColumns(getProductDataColumns(products));
  }, [products]);

  const toggleFields = (data) => {
    return data.map((row) =>
      row.map((td) => {
        if (td.toggleable) {
          return {
            ...td,
            isVisible: !td.isVisible,
          };
        } else {
          return td;
        }
      })
    );
  };

  const [rows, setRows] = useState<any[]>([]);

  const handleClick = () => {
    setProductDataColumns(toggleFields(productDataColumns));
    setShowButtonLabel(
      showButtonLabel === showMoreButtonLabel
        ? showLessButtonLabel
        : showMoreButtonLabel
    );
  };

  useEffect(() => {
    if (setState) {
      setState({
        ...state,
        emulateClick: handleClick,
      });
    }
  }, []);

  useEffect(
    () => setRows(structureDataTableRow(labels, productDataColumns)),
    [productDataColumns]
  );

  const allRows = useMemo(
    () => structureDataTableRow(labels, toggleFields(productDataColumns)),
    [labels]
  );

  const handleLabelSearch = (searchValue) => {
    if (searchValue !== '') {
      filterOptionsHandler((prevState) => {
        if (
          prevState.indexOf(searchValue) < 0 &&
          searchValue.toLowerCase().indexOf(filter?.toLowerCase().trimEnd()) >
            -1
        ) {
          return [...prevState, searchValue];
        } else {
          return prevState.filter(
            (option) =>
              option.toLowerCase().indexOf(filter?.toLowerCase().trimEnd()) !==
              -1
          );
        }
      });
    }
  };

  const handleRowParse = (values, childrenSearch = false) => {
    if (childrenSearch) {
      values.forEach((innerRow) => {
        innerRow.forEach(({ props: { children } }, i) => {
          if (i === 0) {
            handleLabelSearch(children);
          }
        });
      });
    } else {
      values.forEach(({ props: { label } }, i) => {
        if (i === 0) {
          handleLabelSearch(label);
        }
      });
    }
  };

  useEffect(() => {
    if (filter && filter.length > 0) {
      allRows.forEach((row) => {
        if (Array.isArray(row)) {
          handleRowParse(row);
        } else {
          handleRowParse(row.labels);
          handleRowParse(row.rows, true);
        }
      });
    } else {
      filterOptionsHandler([]);
    }
  }, [filter, rows.length]);

  return (
    <Div display="flex" flexDirection="column" {...divProps}>
      <ComparisonTable options={options} rows={rows} />
      {withToggleButton && (
        <Div mt={0} mb={6}>
          <Button variant="transparent" onClick={handleClick}>
            {showButtonLabel}
          </Button>
        </Div>
      )}
    </Div>
  );
};
